import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { Layout } from "shared/utils";
import {
  columnsPaymentSummary,
  PaymentSummaryRow,
} from "./helper/columnsPaymentSummary";
import { formatCurrency } from "shared/utils";
import styles from "./styles/autorizationPayments.module.scss";
import tableStyles from "../../pages/read-documents/styles/tableResults.module.scss";
import { FooterTable } from "shared/components/table/components/FooterTable";
import {
  postPaymentSummary,
  PaymentSummaryResponse,
  postExportPaymentsZip,
  PostExportPaymentsRequest,
} from "shared/api/services";
import { Spinner } from "shared";
import { downloadDocument } from "shared/utils/helpers/downloadDocument";

type LocationState = {
  selectedIds?: number[];
};

export const PaymentProcessSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const state = (location.state || {}) as LocationState;

  const selectedIds = useMemo(
    () => (Array.isArray(state.selectedIds) ? state.selectedIds : []),
    [state.selectedIds],
  );

  const [summary, setSummary] = useState<PaymentSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cdps, setCdps] = useState<Record<string, string>  >({});
  const [selectedCostCenters, setSelectedCostCenters] = useState<
    Record<string, string>
  >({});
  const [rcps, setRcps] = useState<Record<string, string>>({});

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const valuesResultsPages = useMemo(() => [10, 25, 50, 100], []);

  const handleChangePage = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeSizePage = (event: any) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  const handleCdpChange = useCallback(
    (rowId: string, value: string) => {
      setCdps((prev) => ({
        ...prev,
        [rowId]: value,
      }));
    },
    [],
  );

  const handleCostCenterChange = useCallback((rowId: string, value: string) => {
    setSelectedCostCenters((prev) => ({
      ...prev,
      [rowId]: value,
    }));
  }, []);

  const handleRcpChange = useCallback((rowId: string, value: string) => {
    setRcps((prev) => ({
      ...prev,
      [rowId]: value,
    }));
  }, []);

  useEffect(() => {
    const fetchSummary = async () => {
      if (selectedIds.length === 0) return;
      setIsLoading(true);
      try {
        const response = await postPaymentSummary({
          eDocumentIds: selectedIds,
        });
        setSummary(response);
      } catch (_error) {
        setSummary(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, [selectedIds]);

  const tableData: PaymentSummaryRow[] = useMemo(() => {
    if (!summary?.details?.length) return [];

    const data = summary.details.map((detail, idx) => {
      const costCenter = Array.isArray(detail.costCenter)
        ? detail.costCenter
        : [];

      return {
        rowId: `${summary.consecutive}-${idx}`,
        codigoIdentificador: summary.consecutive,
        codigoConcepto: detail.conceptCode,
        concepto: detail.conceptDescription,
        nitPrestador: detail.provider?.employerIdNumber,
        prestador: detail.provider?.name,
        valor: detail.valor,
        signo: detail.operatingIndicator,
        codigoCentroCostos: "",
        nombreCentroCostos: "",
        cdp: detail.cdp ?? "",
        rcp: detail.rcp ?? "",
        codigoMaterial: detail.materialCode,
        costCenterArray: costCenter,
      };
    });

    return data.sort((a, b) => {
      if (a.signo === "+" && b.signo === "-") return -1;
      if (a.signo === "-" && b.signo === "+") return 1;
      return 0;
    });
  }, [summary]);

  const handleApplyCdpToEmpty = useCallback(
    (sourceRowId: string) => {
      const sourceValue = cdps[sourceRowId] || "";
      if (!sourceValue) return;

      setCdps((prev) => {
        const updated = { ...prev };
        tableData.forEach((row) => {
          if (row.rowId && row.rowId !== sourceRowId) {
            const currentValue = prev[row.rowId] || row.cdp || "";
            if (!currentValue) {
              updated[row.rowId] = sourceValue;
            }
          }
        });
        return updated;
      });
    },
    [cdps, tableData],
  );

  const handleApplyRcpToEmpty = useCallback(
    (sourceRowId: string) => {
      const sourceValue = rcps[sourceRowId] || "";
      if (!sourceValue) return;

      setRcps((prev) => {
        const updated = { ...prev };
        tableData.forEach((row) => {
          if (row.rowId && row.rowId !== sourceRowId) {
            const currentValue = prev[row.rowId] || row.rcp || "";
            if (!currentValue) {
              updated[row.rowId] = sourceValue;
            }
          }
        });
        return updated;
      });
    },
    [rcps, tableData],
  );

  const columns = useMemo(
    () =>
      columnsPaymentSummary(
        handleCdpChange,
        cdps,
        handleCostCenterChange,
        selectedCostCenters,
        handleRcpChange,
        rcps,
        handleApplyCdpToEmpty,
        handleApplyRcpToEmpty,
      ),
    [
      handleCdpChange,
      cdps,
      handleCostCenterChange,
      selectedCostCenters,
      handleRcpChange,
      rcps,
      handleApplyCdpToEmpty,
      handleApplyRcpToEmpty,
    ],
  );

  const totalConceptos = useMemo(() => {
    return tableData
      .filter((row) => row.signo === "+")
      .reduce((sum, row) => sum + (row.valor || 0), 0);
  }, [tableData]);

  const totalDeducciones = useMemo(() => {
    return tableData
      .filter((row) => row.signo === "-")
      .reduce((sum, row) => sum + (row.valor || 0), 0);
  }, [tableData]);

  const handleExportPayment = useCallback(async () => {
    if (selectedIds.length === 0 || !summary) return;

    setIsLoading(true);
    try {
      const rows = tableData.map((row) => ({
        consecutive: summary.consecutive || "",
        conceptCode: row.codigoConcepto || "",
        employerIdNumber: row.nitPrestador || "",
        totalValue: row.valor || 0,
        operatingIndicator: row.signo || "",
        costCenterCode: selectedCostCenters[row.rowId || ""] || "",
        budgetCertificate: cdps[row.rowId || ""] || row.cdp || "",
        budgetRegistration: rcps[row.rowId || ""] || row.rcp || "",
        materialCode: row.codigoMaterial || "",
      }));

      const payload: PostExportPaymentsRequest = {
        electronicDocumentIds: selectedIds,
        rows,
      };

      const response = await postExportPaymentsZip(payload);
      downloadDocument(
        response,
        "Exportar para pago",
        "Documento descargado con éxito",
        "success",
      );
      navigate("/autorizaciones");
    } catch (error) {
      console.error("Error exporting payment:", error);
      downloadDocument(
        null,
        "Error en la exportación",
        "No se pudo exportar el documento",
        "error",
      );
      navigate("/autorizaciones");
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedIds,
    summary,
    tableData,
    cdps,
    selectedCostCenters,
    rcps,
    navigate,
  ]);

  return (
    <Layout>
      <section
        className={`${styles.contentAutorizationPayment} ${styles.hideScrollbar}`}
        style={{ justifyContent: "flex-start", padding: "8px 12px" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography className="titleMainLayout">
            Resumen de exporte para pagos
          </Typography>
          <Button
            variant="contained"
            className={styles.authorizationBtnFilters}
            data-testid="btn-volver-modulo-financiero"
            onClick={() => navigate(-1)}
          >
            Volver
          </Button>
        </Box>

        <Card
          sx={{
            border: "1px solid #a1a1a1",
            borderRadius: "12px",
            margin: "0.5rem 0",
            opacity: 1,
            width: "100%",
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{ padding: "12px", alignItems: "flex-end" }}
          >
            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "3px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 0.4rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Consecutivo de pago:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {summary?.consecutive ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "3px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 0.4rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  NIT prestador de salud:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {summary?.provider?.employerIdNumber ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "3px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 0.4rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Prestador de salud:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {summary?.provider?.name ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "3px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 0.4rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Cantidad de facturas exportadas:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {selectedIds.length}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "3px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 0.4rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Total exporte de pago:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {summary?.paymentAuthorizationTotalValue
                    ? `${formatCurrency(summary.paymentAuthorizationTotalValue)}`
                    : "-"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>

        <Box sx={{ height: "40vh", display: "flex", flexDirection: "column" }}>
          <Box className={tableStyles.containerTableResults} sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <TableContainer
              className={tableStyles.tableResults}
              sx={{ overflowX: "auto", overflowY: "auto", flex: 1, maxHeight: "100%" }}
            >
              <Table sx={{ tableLayout: "fixed", width: "100%" }} stickyHeader>
                <TableHead
                  className={tableStyles.tablaHeader}
                  sx={{
                    backgroundColor: "#eef0f3 !important",
                    background: "#eef0f3 !important",
                    "& .MuiTableCell-root": {
                      backgroundColor: "#eef0f3 !important",
                    },
                  }}
                >
                  <TableRow>
                    {columns.map((column, index) => (
                      <TableCell
                        key={column.field}
                        className={
                          index === columns.length - 1
                            ? undefined
                            : tableStyles.tableCell
                        }
                        sx={{
                          backgroundColor: "#eef0f3",
                          border: 0,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color:
                            "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                          padding: "6px",
                          textTransform: "capitalize",
                          whiteSpace: "normal",
                          lineHeight: "1.1",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        {column.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData
                    .slice(
                      (page - 1) * rowsPerPage,
                      (page - 1) * rowsPerPage + rowsPerPage,
                    )
                    .map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          background:
                            "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                          borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                          },
                        }}
                      >
                        {columns.map((column, index) => (
                          <TableCell
                            key={column.field}
                            className={
                              index === columns.length - 1
                                ? undefined
                                : tableStyles.tableCell
                            }
                            sx={{
                              color: "#6d6d6d",
                              fontSize:
                                "clamp(0.75rem, 0.3042rem + 1.48vw, 0.675rem)",
                              fontWeight: 500,
                              padding: "4px 6px",
                              width: `${100 / columns.length}%`,
                            }}
                          >
                            {column.field === "codigoCentroCostos" ? (
                              <Box
                                sx={{
                                  whiteSpace: "normal",
                                  overflow: "hidden",
                                  textOverflow: "initial",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.2",
                                  maxHeight: "2.4em",
                                }}
                              >
                                {column.renderCell
                                  ? column.renderCell(row)
                                  : (row[
                                      column.field as keyof PaymentSummaryRow
                                    ] as string)}
                              </Box>
                            ) : column.field === "cdp" ? (
                              <Box
                                sx={{
                                  whiteSpace: "normal",
                                  overflow: "hidden",
                                  textOverflow: "initial",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.2",
                                  maxHeight: "2.4em",
                                }}
                              >
                                {column.renderCell
                                  ? column.renderCell(row)
                                  : (row[
                                      column.field as keyof PaymentSummaryRow
                                    ] as string)}
                              </Box>
                            ) : column.field === "rcp" ? (
                              <Box
                                sx={{
                                  whiteSpace: "normal",
                                  overflow: "hidden",
                                  textOverflow: "initial",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.2",
                                  maxHeight: "2.4em",
                                }}
                              >
                                {column.renderCell
                                  ? column.renderCell(row)
                                  : (row[
                                      column.field as keyof PaymentSummaryRow
                                    ] as string)}
                              </Box>
                            ) : (
                              <Tooltip
                                title={
                                  column.renderCell
                                    ? column.renderCell(row)
                                    : (row[
                                        column.field as keyof PaymentSummaryRow
                                      ] as string)
                                }
                                arrow
                              >
                                <Box
                                  sx={{
                                    whiteSpace:
                                      column.field === "codigoIdentificador"
                                        ? "nowrap"
                                        : "normal",
                                    overflow: "hidden",
                                    textOverflow:
                                      column.field === "codigoIdentificador"
                                        ? "ellipsis"
                                        : "initial",
                                    display:
                                      column.field === "codigoIdentificador"
                                        ? "block"
                                        : "-webkit-box",
                                    WebkitLineClamp:
                                      column.field === "codigoIdentificador"
                                        ? 1
                                        : 2,
                                    WebkitBoxOrient:
                                      column.field === "codigoIdentificador"
                                        ? "horizontal"
                                        : "vertical",
                                    lineHeight: "1.2",
                                    maxHeight:
                                      column.field === "codigoIdentificador"
                                        ? "auto"
                                        : "2.4em",
                                  }}
                                >
                                  {column.renderCell
                                    ? column.renderCell(row)
                                    : (row[
                                        column.field as keyof PaymentSummaryRow
                                      ] as string)}
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <FooterTable
              handleChangePage={handleChangePage}
              handleChangeSizePage={handleChangeSizePage}
              numberPage={page}
              size={rowsPerPage}
              sizeRows={rowsPerPage.toString()}
              totalElements={tableData.length}
              valuesResultsPages={valuesResultsPages}
              contentSize={Math.min(page * rowsPerPage, tableData.length)}
            />
          </Box>
        </Box>

        <Box>
          <Card
            sx={{
              border: "1px solid #a1a1a1",
              borderRadius: "12px",
              margin: "0.5rem 0",
              opacity: 1,
              width: "100%",
            }}
          >
            <Box sx={{ padding: "8px" }}>
              <Grid container spacing={1}>
                <Grid item xs={6}></Grid>
                <Grid item xs={3}>
                  <Box
                    sx={{
                      paddingBottom: "3px",
                      borderBottom: "1px solid #a1a1a1",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.81rem",
                        color: "#6D6D6D",
                        fontWeight: 500,
                        fontFamily: "Poppins",
                        padding: "0.5rem 0 0.4rem",
                        width: "83%",
                        display: "flex",
                        flexDirection: "row",
                        gap: "0.5rem",
                      }}
                    >
                      Total pagos:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#969696",
                        fontWeight: 500,
                        paddingLeft: "5px",
                      }}
                    >
                      {totalConceptos
                        ? `${formatCurrency(totalConceptos)}`
                        : "-"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={3}>
                  <Box
                    sx={{
                      paddingBottom: "3px",
                      borderBottom: "1px solid #a1a1a1",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.81rem",
                        color: "#6D6D6D",
                        fontWeight: 500,
                        fontFamily: "Poppins",
                        padding: "0.5rem 0 0.4rem",
                        width: "83%",
                        display: "flex",
                        flexDirection: "row",
                        gap: "0.5rem",
                      }}
                    >
                      Total deducciones:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#969696",
                        fontWeight: 500,
                        paddingLeft: "5px",
                      }}
                    >
                      {totalDeducciones
                        ? `${formatCurrency(totalDeducciones)}`
                        : "-"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            className={styles.authorizationBtnFilters}
            data-testid="btn-exportar-pago"
            startIcon={<Download />}
            sx={{ minWidth: "230px" }}
            onClick={handleExportPayment}
          >
            Exportar para pago
          </Button>
        </Box>
      </section>
      {isLoading && <Spinner />}
    </Layout>
  );
};
